import { ParsedUrlQuery } from 'querystring';
import { google } from '@morf/proto/struct_ts_proto';
import { Params } from './useUrlParams';

/**
 * Extracts all non-scheduling query parameters from the URL query object.
 * This allows customers to pass tracking parameters (UTM codes, custom IDs, etc.)
 * through to Morf workflows via the event_data field.
 *
 * Also supports a `metadata` parameter containing URL-encoded JSON for complex data.
 * Metadata keys are merged with individual params, with individual params taking precedence.
 *
 * @param query - The Next.js router query object (all URL params)
 * @param schedulingParams - The parsed scheduling params from useUrlParams()
 * @returns A Struct containing all non-scheduling query parameters, or undefined if none exist
 */
export const getNonSchedulingQueryParams = (
  query: ParsedUrlQuery,
  schedulingParams: Params
): google.protobuf.Struct | undefined => {
  const schedulingKeys = new Set(Object.keys(schedulingParams));
  const nonSchedulingParams: Record<string, string | string[]> = {};

  const metadataParam = query.metadata;
  if (typeof metadataParam === 'string' && metadataParam.trim() !== '') {
    try {
      const decodedMetadata = decodeURIComponent(metadataParam);
      const parsedMetadata = JSON.parse(decodedMetadata);

      if (
        parsedMetadata &&
        typeof parsedMetadata === 'object' &&
        !Array.isArray(parsedMetadata)
      ) {
        for (const [key, value] of Object.entries(parsedMetadata)) {
          if (value !== undefined && value !== null) {
            nonSchedulingParams[key] = String(value);
          }
        }
      }
    } catch (error) {
      // Silent fallback - invalid JSON is ignored
    }
  }

  // Extract all query parameters that are not scheduling-related
  // These override any metadata keys with the same name
  for (const [key, value] of Object.entries(query)) {
    if (!schedulingKeys.has(key) && key !== 'metadata' && value !== undefined) {
      nonSchedulingParams[key] = value;
    }
  }

  // Return undefined if there are no non-scheduling params
  if (Object.keys(nonSchedulingParams).length === 0) {
    return undefined;
  }

  // Convert to Struct format
  const fields: Record<string, google.protobuf.Value> = {};
  for (const [key, value] of Object.entries(nonSchedulingParams)) {
    if (Array.isArray(value)) {
      // Handle array values
      fields[key] = new google.protobuf.Value({
        listValue: new google.protobuf.ListValue({
          values: value.map(
            (v) => new google.protobuf.Value({ stringValue: v })
          ),
        }),
      });
    } else {
      // Handle string values
      fields[key] = new google.protobuf.Value({ stringValue: value });
    }
  }

  return new google.protobuf.Struct({ fields });
};
